import React, { useEffect, useRef } from 'react'

// Autoplaying background/artwork video.
//
// Why this exists rather than a plain <video autoPlay muted>:
//
// React sets `muted` as a DOM *property* but never emits the `muted` HTML
// *attribute*. Chrome's autoplay policy inspects the attribute when it decides
// whether an autoplaying video is eligible to start without a user gesture, so
// a JSX `<video autoPlay muted>` is frequently blocked. Whether it plays then
// depends on the viewer's Media Engagement Index — meaning it plays for people
// who visit often (like us) and stays frozen for first-time visitors, which is
// exactly the kind of bug that never reproduces locally.
//
// The fix is to set the attribute imperatively on mount and then ask for
// playback explicitly, tolerating a rejection.
const AutoVideo = ({ src, poster, children, ...rest }) => {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    el.muted = true
    el.setAttribute('muted', '')

    const attempt = el.play()
    // Older browsers return undefined; a rejection just means the policy said
    // no. The poster stays visible in that case, so there is nothing to do.
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {})
    }
  }, [src])

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      controlsList="nodownload"
      {...rest}
    >
      {children}
    </video>
  )
}

export default AutoVideo
